'use client'

import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'
import { TransactionErrorPanel } from '@/ui/molecules/transaction-error-panel/TransactionErrorPanel'
import { getTokenImage } from '@/ui/assets/tokens'
import { assets } from '@/ui/assets'
import { Slider } from '@/ui/atoms/slider/Slider'
import Image from 'next/image'
import { cn } from '@/ui/utils/style'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { useState, useEffect } from 'react'
import { normalizeDecimalInput } from '@/utils/decimalInput'

interface EWithdrawCollateralViewProps {
  collateralSymbol: string
  collateralLogoURI?: string
  collateralDecimals: number
  withdrawAmount: string
  setWithdrawAmount: (amount: string) => void
  currentCollateralFormatted: number
  maxWithdrawable: number
  currentLTV: number
  newLTV: number
  maxLTV: number
  liquidationLTV: number
  unwrapToNative: boolean
  setUnwrapToNative: (unwrap: boolean) => void
  isWhypeCollateral: boolean
  collateralPriceUSD: number
  wouldLiquidate: boolean
  withdrawStatus: ActionHandlerState['status']
  handleExecute: () => void
  isSimulating: boolean
  displayError: Error | null
  isTimedOut: boolean
}

export function EWithdrawCollateralView({
  collateralSymbol,
  collateralLogoURI,
  collateralDecimals,
  withdrawAmount,
  setWithdrawAmount,
  currentCollateralFormatted,
  maxWithdrawable,
  currentLTV,
  newLTV,
  maxLTV,
  liquidationLTV,
  unwrapToNative,
  setUnwrapToNative,
  isWhypeCollateral,
  collateralPriceUSD,
  wouldLiquidate,
  withdrawStatus,
  handleExecute,
  isSimulating,
  displayError,
  isTimedOut,
}: EWithdrawCollateralViewProps) {
  const displaySymbol = unwrapToNative ? 'HYPE' : collateralSymbol
  const tokenImage = collateralLogoURI || getTokenImage(TokenSymbol(displaySymbol))

  const [inputValue, setInputValue] = useState(withdrawAmount)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [sliderValue, setSliderValue] = useState(0)

  useEffect(() => {
    if (!isInputFocused) {
      setInputValue(withdrawAmount)
    }
  }, [withdrawAmount, isInputFocused])

  useEffect(() => {
    const amount = Number(withdrawAmount) || 0
    if (maxWithdrawable > 0) {
      setSliderValue((amount / maxWithdrawable) * 100)
    }
  }, [withdrawAmount, maxWithdrawable])

  const hasDebt = currentLTV > 0 || maxLTV > 0
  const withdrawAmountNum = Number(withdrawAmount) || 0
  const withdrawValueUSD = withdrawAmountNum * collateralPriceUSD
  const isNearMaxLTV = hasDebt && withdrawAmountNum > 0 && newLTV > maxLTV * 0.95
  const exceedsMaxLTV = hasDebt && newLTV > maxLTV
  const exceedsCollateral = withdrawAmountNum > currentCollateralFormatted

  const currentHealthFactor = currentLTV > 0 ? liquidationLTV / currentLTV : Number.POSITIVE_INFINITY
  const newHealthFactor = newLTV > 0 ? liquidationLTV / newLTV : Number.POSITIVE_INFINITY

  const getHealthStatus = (hf: number) => {
    if (!Number.isFinite(hf) || hf >= 3) return { label: 'Healthy', color: 'text-teal-400', icon: TrendingUp }
    if (hf >= 2) return { label: 'Moderate', color: 'text-amber-400', icon: TrendingUp }
    if (hf >= 1) return { label: 'Risky', color: 'text-red-400', icon: TrendingDown }
    return { label: 'Liquidation', color: 'text-red-400', icon: TrendingDown }
  }

  const newHealthStatus = getHealthStatus(newHealthFactor)

  const handleSliderChange = (values: number[]) => {
    const percentage = values[0] ?? 0
    setSliderValue(percentage)
    const amount = (percentage / 100) * maxWithdrawable
    setWithdrawAmount(amount.toFixed(collateralDecimals))
  }

  return (
    <MultiPanelDialog>
      <DialogTitle className="font-semibold text-white text-xl">
        {hasDebt ? 'Withdraw Collateral' : 'Withdraw'}
      </DialogTitle>

      <DialogPanel>
        <div className="flex flex-col gap-4">
          {/* Asset selector for WHYPE vaults */}
          {isWhypeCollateral && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setUnwrapToNative(true)}
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors',
                  unwrapToNative ? 'border-primary bg-primary/10' : 'border-white/10 bg-white/5 hover:border-white/20',
                )}
              >
                <Image
                  src={getTokenImage(TokenSymbol('HYPE'))}
                  alt="HYPE"
                  width={20}
                  height={20}
                  className="rounded-full"
                />
                <span className="text-sm text-white">Receive HYPE</span>
              </button>
              <button
                type="button"
                onClick={() => setUnwrapToNative(false)}
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors',
                  !unwrapToNative ? 'border-primary bg-primary/10' : 'border-white/10 bg-white/5 hover:border-white/20',
                )}
              >
                <Image
                  src={collateralLogoURI || getTokenImage(TokenSymbol('WHYPE'))}
                  alt="WHYPE"
                  width={20}
                  height={20}
                  className="rounded-full"
                />
                <span className="text-sm text-white">Receive WHYPE</span>
              </button>
            </div>
          )}

          {/* Current Position Info */}
          <div className="flex flex-col gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">
                {hasDebt ? 'Current Collateral' : 'Current Balance'}
              </span>
              <span className="text-sm text-white">
                {currentCollateralFormatted.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}{' '}
                {collateralSymbol}
              </span>
            </div>
            {hasDebt && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">Current Health</span>
                <span className="text-sm text-teal-400">
                  {Number.isFinite(currentHealthFactor) ? currentHealthFactor.toFixed(2) : '>100'}
                </span>
              </div>
            )}
          </div>

          {/* Withdraw Amount Input */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-muted-foreground text-xs">Amount to Withdraw</label>
              <button
                type="button"
                onClick={() => setWithdrawAmount(maxWithdrawable.toFixed(collateralDecimals))}
                className="text-primary text-xs hover:underline"
              >
                Max: {maxWithdrawable.toLocaleString(undefined, { maximumFractionDigits: 6 })} {collateralSymbol}
              </button>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-3">
              {tokenImage && (
                <Image src={tokenImage} alt={displaySymbol} width={32} height={32} className="rounded-full" />
              )}
              <input
                type="text"
                inputMode="decimal"
                value={inputValue}
                onChange={(e) => {
                  const normalized = normalizeDecimalInput(e.target.value)
                  if (normalized !== null) {
                    setInputValue(normalized)
                    setWithdrawAmount(normalized || '0')
                  }
                }}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => {
                  setIsInputFocused(false)
                  setInputValue(withdrawAmount)
                }}
                className="flex-1 bg-transparent text-lg text-white outline-none placeholder:text-white/30"
                placeholder="0.00"
              />
              <span className="text-muted-foreground">{displaySymbol}</span>
            </div>
            {withdrawAmountNum > 0 && (
              <div className="text-right text-muted-foreground text-xs">
                ${withdrawValueUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            )}
          </div>

          {/* Slider */}
          <div className="flex flex-col gap-2">
            <Slider value={[sliderValue]} max={100} min={0} step={0.1} onValueChange={handleSliderChange} />
            <div className="flex items-center justify-between text-white/30 text-xs">
              <span>0%</span>
              <span>100% (Max)</span>
            </div>
          </div>

          {/* Warning for high LTV */}
          {isNearMaxLTV && !exceedsMaxLTV && (
            <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3 text-sm text-yellow-400">
              <p>This withdrawal will bring your position close to the maximum LTV.</p>
            </div>
          )}

          {/* Error for liquidation (withdrawing all collateral with debt) */}
          {wouldLiquidate && !exceedsCollateral && (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-red-400 text-sm">
              <p>
                This withdrawal would remove all collateral while you have outstanding debt, triggering immediate
                liquidation.
              </p>
            </div>
          )}

          {/* Error for exceeding max LTV */}
          {exceedsMaxLTV && !exceedsCollateral && !wouldLiquidate && (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-red-400 text-sm">
              <p>This withdrawal would exceed the maximum LTV of {maxLTV.toFixed(2)}%.</p>
            </div>
          )}

          {/* Error for exceeding deposited collateral */}
          {exceedsCollateral && (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-red-400 text-sm">
              <p>
                You only have {currentCollateralFormatted.toLocaleString(undefined, { maximumFractionDigits: 6 })}{' '}
                {collateralSymbol} deposited.
              </p>
            </div>
          )}

          {/* Health Score & LTV - only show when position has debt */}
          {hasDebt && (
            <div className="flex flex-col gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">New Health Score</span>
                <div className="flex items-center gap-2">
                  {withdrawAmountNum > 0 ? (
                    <>
                      <newHealthStatus.icon className={cn('h-4 w-4', newHealthStatus.color)} />
                      <span className={cn('font-semibold text-sm', newHealthStatus.color)}>
                        {wouldLiquidate
                          ? 'Liquidation'
                          : Number.isFinite(newHealthFactor)
                            ? newHealthFactor > 100
                              ? '>100'
                              : newHealthFactor.toFixed(2)
                            : '>100'}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-white">-</span>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">New LTV</span>
                <span
                  className={cn(
                    'font-medium text-sm',
                    wouldLiquidate || exceedsMaxLTV ? 'text-red-400' : isNearMaxLTV ? 'text-amber-400' : 'text-white',
                  )}
                >
                  {withdrawAmountNum > 0 ? (wouldLiquidate ? 'Liquidation' : `${newLTV.toFixed(2)}%`) : '-'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">Max LTV</span>
                <span className="text-xs text-yellow-500/70">{maxLTV.toFixed(2)}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">Liquidation LTV</span>
                <span className="text-red-400/70 text-xs">{liquidationLTV.toFixed(2)}%</span>
              </div>
            </div>
          )}

          {/* Summary */}
          {withdrawAmountNum > 0 && !exceedsMaxLTV && (
            <div className="mt-2 flex flex-col gap-2">
              <TransactionOverviewDetailsItem label="Amount to withdraw">
                {withdrawAmountNum.toLocaleString(undefined, { maximumFractionDigits: 6 })} {displaySymbol}
              </TransactionOverviewDetailsItem>
              <TransactionOverviewDetailsItem label={hasDebt ? 'Remaining collateral' : 'Remaining balance'}>
                {Math.max(0, currentCollateralFormatted - withdrawAmountNum).toLocaleString(undefined, {
                  maximumFractionDigits: 6,
                })}{' '}
                {collateralSymbol}
              </TransactionOverviewDetailsItem>
            </div>
          )}

          <TimeoutErrorDisplay
            isTimedOut={isTimedOut}
            onRetry={handleExecute}
            isRetryDisabled={withdrawStatus === 'loading'}
          />

          {!isTimedOut && <TransactionErrorPanel error={displayError} />}

          <BigBlocksBanner className="mt-4" />

          {/* EVC Action Row */}
          <div className="mt-2 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
            <ActionRow index={1}>
              <ActionRow.Icon path={assets.actions.withdraw} actionStatus={withdrawStatus} />
              <ActionRow.Title actionStatus={withdrawStatus}>
                {unwrapToNative ? 'Withdraw & Unwrap' : 'Withdraw'}
              </ActionRow.Title>
              <ActionRow.Description
                successMessage={unwrapToNative ? 'Withdrawn & Unwrapped!' : 'Withdrawn!'}
                actionStatus={withdrawStatus}
                variant="compact"
              />
              <ActionRow.Action onAction={handleExecute} status={withdrawStatus}>
                {isTimedOut ? 'Retry' : isSimulating ? 'Preparing...' : 'Withdraw'}
              </ActionRow.Action>
            </ActionRow>
          </div>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}
